// src/components/Common/Accordion.js
import React, { useState } from "react";
import styled from "styled-components";

/**
 * Accordion
 * Componente estándar de acordeón para APIM.
 * Basado en la arquitectura visual de /maestros (admin - usuario).
 * 
 * Props:
 * @param {React.ReactNode} title - Título del encabezado.
 * @param {number|string} [count] - Contador o badge numérico opcional.
 * @param {boolean} [defaultOpen=true] - Estado inicial abierto/cerrado en modo no controlado.
 * @param {boolean} [open] - Estado abierto/cerrado en modo controlado.
 * @param {function} [onToggle] - Callback (isOpen) al abrir o cerrar.
 * @param {React.ReactNode} [rightAction] - Elementos adicionales en el encabezado (ej. botones/iconos).
 * @param {boolean} [showArrow=true] - Muestra la flecha indicadora ▲ / ▼.
 * @param {boolean} [fillSpace=false] - Si es true, aplica flex: 1 y minHeight: 0 para layouts como sidebar.
 * @param {string} [fontSize="11px"] - Tamaño de fuente del encabezado.
 * @param {string} [headerPadding="5px 10px"] - Padding del botón del encabezado.
 * @param {string} [bodyPadding="6px 8px"] - Padding del contenedor de contenido.
 * @param {string} [headerColor="#1e40af"] - Color de texto del encabezado.
 * @param {object} [style] - Estilos adicionales para el contenedor exterior.
 * @param {object} [headerStyle] - Estilos adicionales para el encabezado.
 * @param {object} [bodyStyle] - Estilos adicionales para el cuerpo del acordeón.
 */

const AccordionContainer = styled.div`
  margin-bottom: ${(props) => (props.$fillSpace ? "0" : "6px")};
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background-color: #ffffff;
  transition: all 0.2s ease;
  box-sizing: border-box;
  ${(props) =>
    props.$fillSpace &&
    `
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: ${props.$isOpen ? "1" : "0 0 auto"};
    overflow: hidden;
  `}
`;

const HeaderButton = styled.button.attrs({ type: "button" })`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.$headerPadding || "5px 10px"};
  background: ${(props) => (props.$isOpen ? "#f1f5f9" : "#f8fafc")};
  border: none;
  border-radius: ${(props) => (props.$isOpen ? "6px 6px 0 0" : "6px")};
  cursor: pointer;
  font-size: ${(props) => props.$fontSize || "11px"};
  font-weight: 600;
  color: ${(props) => props.$headerColor || "#1e40af"};
  font-family: var(--font-body, inherit);
  transition: background-color 0.15s ease;
  user-select: none;
  flex-shrink: 0;

  &:hover {
    background: #e2e8f0;
  }
`;

const TitleWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  text-align: left;
`;

const CountBadge = styled.span`
  background: #dbeafe;
  color: #2563eb;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const RightWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ArrowIndicator = styled.span`
  font-size: 10px;
  color: #94a3b8;
  display: inline-block;
  transition: transform 0.2s ease;
  line-height: 1;
`;

const BodyContainer = styled.div`
  padding: ${(props) => props.$bodyPadding || "6px 8px"};
  border-top: 1px solid #e2e8f0;
  background-color: #ffffff;
  ${(props) =>
    props.$fillSpace &&
    `
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f8fafc;

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: #f8fafc;
    }
    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #94a3b8;
    }
  `}
`;

export default function Accordion({
  title,
  count,
  children,
  defaultOpen = true,
  open: controlledOpen,
  onToggle,
  rightAction,
  showArrow = true,
  fillSpace = false,
  fontSize = "11px",
  headerPadding = "5px 10px",
  bodyPadding = "6px 8px",
  headerColor = "#1e40af",
  style = {},
  headerStyle = {},
  bodyStyle = {},
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const handleToggle = (e) => {
    const nextState = !isOpen;
    if (!isControlled) {
      setInternalOpen(nextState);
    }
    if (onToggle) {
      onToggle(nextState, e);
    }
  };

  return (
    <AccordionContainer
      $fillSpace={fillSpace}
      $isOpen={isOpen}
      style={style}
    >
      <HeaderButton
        $isOpen={isOpen}
        $fontSize={fontSize}
        $headerPadding={headerPadding}
        $headerColor={headerColor}
        onClick={handleToggle}
        style={headerStyle}
      >
        <TitleWrapper>
          {typeof title === "string" ? <span>{title}</span> : title}
          {count != null && <CountBadge>{count}</CountBadge>}
        </TitleWrapper>

        <RightWrapper>
          {rightAction && (
            <span
              onClick={(e) => e.stopPropagation()}
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              {rightAction}
            </span>
          )}
          {showArrow && (
            <ArrowIndicator>{isOpen ? "▲" : "▼"}</ArrowIndicator>
          )}
        </RightWrapper>
      </HeaderButton>

      {isOpen && (
        <BodyContainer
          $fillSpace={fillSpace}
          $bodyPadding={bodyPadding}
          style={bodyStyle}
        >
          {children}
        </BodyContainer>
      )}
    </AccordionContainer>
  );
}
